import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { CreatableSelect, InfoTip } from '../../ui';
import { tokens } from '../../../theme/tokens';
import {
  UNMAPPED_CATEGORY_UNSET,
  UNMAPPED_CATEGORY_UNSET_LABEL,
  unmappedCategoryNote,
  type FallbackMode,
} from '../mappingModel';
import CategorySelect from './CategorySelect';
import { GroupLabel, StepCircle, v2CardSx } from './v2Ui';

const OPTIONS: Array<{ mode: FallbackMode; label: string; hint: string; tip: string }> = [
  { mode: 'derive', label: 'Derive from Motzu Classification', hint: 'prime-macro → Prime Macro', tip: 'Pick this when Motzu\'s classification names are already readable in your store. Nothing needs setting up.' },
  { mode: 'default', label: 'Use a default type', hint: 'Every unmatched product gets the same type.', tip: 'Pick this when you want one catch-all type, such as accessory. Handy if you filter or build collections on it.' },
  { mode: 'flag', label: 'Flag as [UNMAPPED]', hint: 'Prefix the type so you can find these in Shopify.', tip: 'Pick this while you set up, or to keep exceptions visible. Search Shopify for [UNMAPPED] to find what still needs a Store Taxonomy entry, a Name Rule or an override.' },
];

interface FallbackStepProps {
  n: number;
  mode: FallbackMode;
  onMode: (mode: FallbackMode) => void;
  defaultProductType: string;
  onDefaultProductType: (value: string) => void;
  typeOptions: string[];
  /** The Shopify category a product that matches nothing gets; empty means the neutral default. */
  unmappedGid: string;
  neutralGid: string | undefined;
  taxonomyOptions: Array<{ gid: string; label: string }>;
  onUnmappedGid: (gid: string) => void;
  /** Flag `shopify_unmapped_category_unset`: offer "Don't set", which leaves Shopify's category as it is. */
  allowUnset?: boolean;
  loadFailed?: boolean;
}

/** Step 4, Unmatched Product Rules: what a product gets when nothing above decided it. One card for both fields. */
const FallbackStep: React.FC<FallbackStepProps> = ({
  n,
  mode,
  onMode,
  defaultProductType,
  onDefaultProductType,
  typeOptions,
  unmappedGid,
  neutralGid,
  taxonomyOptions,
  onUnmappedGid,
  allowUnset = false,
  loadFailed,
}) => {
  // With the flag off the sync ignores a stored "Don't set" and sends the neutral root, so the select shows that.
  const storedUnset = unmappedGid === UNMAPPED_CATEGORY_UNSET;
  const unset = allowUnset && storedUnset;
  const selected = unset ? UNMAPPED_CATEGORY_UNSET : (storedUnset ? '' : unmappedGid) || neutralGid || '';
  const categoryOptions = allowUnset
    ? [{ gid: UNMAPPED_CATEGORY_UNSET, label: UNMAPPED_CATEGORY_UNSET_LABEL }, ...taxonomyOptions]
    : taxonomyOptions;
  return (
    <Box sx={{ ...v2CardSx, px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <StepCircle n={n} tone="caution" />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Unmatched Product Rules</Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: '3px' }}>
            When to use: choose what a product gets when no Store Taxonomy entry, Name Rule or Motzu Classification Override decided it.
          </Typography>
        </Box>
      </Box>

      <GroupLabel>Shopify Product Type</GroupLabel>
      <Box role="radiogroup" aria-label="Shopify Product Type for unmatched products" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))' }, gap: 1 }}>
        {OPTIONS.map((option) => {
          const on = mode === option.mode;
          return (
            <Box
              key={option.mode}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: on ? tokens.accent.border : tokens.line.default,
                bgcolor: on ? tokens.signal.info.row : tokens.surface.card,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <ButtonBase
                  role="radio"
                  aria-checked={on}
                  onClick={() => onMode(option.mode)}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, textAlign: 'left', flex: 1, minWidth: 0, justifyContent: 'flex-start' }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: '0 0 auto',
                      mt: '2px',
                      display: 'grid',
                      placeItems: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '1.5px solid',
                      borderColor: on ? tokens.accent.main : tokens.ink.disabled,
                    }}
                  >
                    {on && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.accent.main }} />}
                  </Box>
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box component="span" sx={{ fontSize: 13.5, fontWeight: 700, color: tokens.ink.primary }}>{option.label}</Box>
                    <Box component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{option.hint}</Box>
                  </Box>
                </ButtonBase>
    <InfoTip label={`When to pick ${option.label}`} title={option.tip} />
              </Box>
              {on && option.mode === 'default' && (
                <CreatableSelect
                  label=""
                  aria-label="Default Shopify Product Type"
                  value={defaultProductType}
                  options={typeOptions}
                  onChange={onDefaultProductType}
                  placeholder="e.g. accessory"
                  helperText={defaultProductType.trim() ? undefined : 'Until you pick a type, unmatched products keep deriving from their category.'}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <GroupLabel>Shopify Product Category</GroupLabel>
      <Box sx={{ maxWidth: 360 }}>
        <CategorySelect
          aria-label="Shopify Product Category for unmatched products"
          value={selected}
          options={categoryOptions}
          onChange={(gid) => onUnmappedGid(gid === neutralGid ? '' : gid)}
          sx={{ height: 40 }}
        />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
        {unmappedCategoryNote(allowUnset, unset)}
      </Typography>
      {loadFailed && (
        <Typography sx={{ fontSize: 12, color: tokens.signal.caution.ink }}>
          Could not load the Shopify Product Category list. Rows show no default until it loads.
        </Typography>
      )}
    </Box>
  );
};

export default FallbackStep;
